import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock, Mic2 } from 'lucide-react';
import { useSiteContent } from '../content/SiteContentProvider';
import { getItems, getText, getTextList } from '../content/cmsAccess';
import MediaImage from '../components/MediaImage';
import { renderInlineMarkdown } from '../lib/inlineMarkdown';
import { Reveal } from '../components/Reveal';
import { CornerDecoration, WatermarkS } from '../components/Watermark';
import { Sparkles } from '../components/Sparkles';
import GooeyText from '../components/GooeyText';
import LazyYouTubeEmbed from '../components/LazyYouTubeEmbed';
import { CountUpValue } from '../components/CountUpValue';
import MazeToggle from '../components/MazeToggle';
import DiagonalFormulaImage from '../components/DiagonalFormulaImage';

export default function Home() {
  const { content } = useSiteContent();
  const page = content.pages.home;
  const hero = page.hero;
  const activities = page.activities;
  const stats = page.stats;
  const formulasSection = page.formulas;
  const local = page.local;
  const formulas = getItems(formulasSection, 'cards');
  const soonActivities = getItems(activities, 'soonItems');
  const remainingSoonActivities = soonActivities.filter((activity) => !activity.title.toLowerCase().includes('karaok'));
  const localParagraphs = getTextList(local, 'paragraphs');
  const rawActivitiesText = getText(activities, 'text');
  const activitiesText = /karaoké[\s\S]*très bientôt/i.test(rawActivitiesText)
    ? "Préparez-vous à vivre une **expérience inédite** chez **Gameside à Caen** ! Enchaînez les défis de notre **Action Game** ou prenez le micro dans notre **karaoké privé**. À vous de choisir votre activité."
    : rawActivitiesText;

  return (
    <>
      <div
        className="relative w-full h-[100vh] min-h-[700px] flex items-center overflow-hidden bg-[#14151f] pt-32 sm:pt-28 lg:pt-24"
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 5vw), 0 100%)' }}
      >
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <MediaImage
            src={getText(hero, 'image', content.images.home.hero)}
            alt="Activité Caen Action Game Gameside à Ifs"
            title="Activité Caen Action Game Gameside"
            className="w-full h-full object-cover opacity-100"
            priority
            sizes="100vw"
            style={{
              maskImage: 'linear-gradient(to right, black 0%, black 84%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, black 0%, black 84%, transparent 100%)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14151f] via-[#14151f]/78 lg:via-[#14151f]/40 to-transparent w-[90%] lg:w-[58%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14151f] via-transparent to-[#14151f]/45" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col -mt-16">
          <div className="max-w-4xl">
            <Reveal delay={0.1}>
              <h1 className="text-white text-6xl sm:text-7xl lg:text-[90px] font-black uppercase leading-[1.05] mb-6 drop-shadow-2xl flex flex-wrap items-center gap-x-4 lg:gap-x-8">
                <Sparkles>{getText(hero, 'titleLine1', 'GAMESIDE')}</Sparkles>
                <Sparkles className="text-yellow-400">{getText(hero, 'titleLine2', 'CAEN')}</Sparkles>
              </h1>
            </Reveal>

            <p className="text-gray-200 text-lg sm:text-2xl lg:text-3xl font-semibold uppercase leading-snug max-w-2xl border-l-4 border-yellow-400 pl-6 drop-shadow-lg mb-10">
              Votre Gameside à Caen vous attend pour passer un moment unique entre amis, en famille ou avec vos collègues. Venez vous défier sur l'Action Game, fêter un anniversaire ou organiser votre événement d'entreprise.
            </p>

            <div className="flex flex-wrap gap-4 mt-6">
              <Link
                to={getText(hero, 'primaryCtaHref', '/nos-activites/action-game#reservation')}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-[#1a1b24] font-extrabold text-sm sm:text-base px-8 py-3.5 rounded-full flex items-center justify-center gap-3 shadow-md uppercase"
              >
                {getText(hero, 'primaryCtaLabel', 'RÉSERVER UNE SESSION')}
                <ArrowRight size={18} strokeWidth={3} />
              </Link>
              <Link
                to={getText(hero, 'secondaryCtaHref', '/nos-activites/action-game')}
                className="border-2 border-white/30 hover:border-white text-white font-bold text-sm sm:text-base px-8 py-3.5 rounded-full flex items-center justify-center gap-3 uppercase backdrop-blur-sm bg-black/20"
              >
                {getText(hero, 'secondaryCtaLabel', "VOIR L'ACTION GAME")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="relative w-full bg-white pt-8 pb-24 px-6 lg:px-12 overflow-hidden">
        <CornerDecoration variant="dots" className="absolute left-8 top-12 w-12 h-12 hidden md:block text-yellow-400" opacity={0.4} />
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-center">
          <div className="w-full max-w-4xl text-center md:text-left mb-16">
            <Reveal>
              <div className="flex flex-row items-end justify-between gap-3 mb-6">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#1a1b24] uppercase leading-tight">
                  {getText(activities, 'title', 'LES ACTIVITÉS DU GAMESIDE')}
                </h2>
                <MazeToggle className="shrink-0" />
              </div>
            </Reveal>
            <p className="text-gray-600 leading-relaxed text-sm md:text-base">
              {renderInlineMarkdown(activitiesText)}
            </p>
          </div>

          <div className="w-full max-w-7xl">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-7">
              <ActivityFeatureCard
                href="/nos-activites/action-game"
                image={getText(activities, 'actionImage', content.images.actionGame.intro)}
                logo={getText(activities, 'actionLogo', content.brand.logos.actionGame)}
                title={getText(activities, 'actionTitle', 'Action Game')}
                text={getText(activities, 'actionText')}
                icon="action"
              />
              <ActivityFeatureCard
                href="/nos-activites/karaoke"
                image={getText(content.pages.karaoke.card, 'image')}
                title={getText(content.pages.karaoke.card, 'title')}
                text={getText(content.pages.karaoke.card, 'text')}
                icon="karaoke"
              />
            </div>
            {remainingSoonActivities.length > 0 && (
              <div className="mx-auto mt-8 grid w-full max-w-xl grid-cols-1 gap-6">
                {remainingSoonActivities.map((activity) => (
                <ComingSoonCard key={activity.title} title={activity.title} text={activity.text} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <WhyComeSection
        desktopImage={getText(stats, 'image', content.images.actionGame.stats)}
        facts={getItems(stats, 'items')}
      />

      <section
        className="relative w-full overflow-hidden"
        style={{ clipPath: 'polygon(0 5vw, 100% 0, 100% calc(100% - 5vw), 0 100%)' }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center bg-parallax"
          style={{ backgroundImage: `url(${content.images.teamBuilding.hero})` }}
        />
        <div className="absolute inset-0 bg-[#1a2236]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a2236]/85 via-[#1a2236]/55 to-[#1a2236]/35" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-white">
          <div className="max-w-4xl">
            <Reveal>
              <p className="gameside-accent font-black uppercase tracking-[0.22em] text-xs sm:text-sm mb-5">Pour les entreprises</p>
            </Reveal>
            <Reveal delay={0.1} width="100%">
              <h2 className="text-white font-black uppercase tracking-tight mb-7 leading-tight">
                <span className="block relative w-full text-4xl sm:text-5xl md:text-6xl lg:text-7xl h-[1.4em] mb-3 overflow-visible">
                  <GooeyText
                    align="left"
                    texts={['Team Building', 'Privatisation', 'Séminaire', 'Soirées', 'Cohésion']}
                    morphTime={1.1}
                    cooldownTime={1.8}
                    textClassName="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight text-yellow-400 whitespace-nowrap"
                  />
                </span>
                <span className="block gameside-accent text-3xl md:text-4xl lg:text-5xl">à Caen</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mb-12">
                Organisez un événement professionnel à Caen avec notre <strong className="font-black text-white">formule clé en main</strong>. De la privatisation du complexe à l'animation de votre séminaire, nous prenons en charge l'aspect logistique pour que <strong className="font-black text-white">vos équipes</strong> profitent pleinement de leur journée.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 mb-12">
              <Reveal delay={0.08} width="100%">
                <div className="flex flex-col sm:flex-row md:flex-col items-center sm:items-start md:items-center gap-4 sm:gap-5 text-center sm:text-left md:text-center">
                  <div className="shrink-0 w-32 h-32 sm:w-36 sm:h-36 lg:w-44 lg:h-44 xl:w-48 xl:h-48 flex items-center justify-center">
                    <MediaImage src="/brand/business/extraordinary.png" alt="Activités uniques en France" width={384} height={307} className="w-full h-full object-contain drop-shadow-[0_10px_22px_rgba(0,0,0,0.4)]" sizes="(min-width: 1280px) 12rem, (min-width: 1024px) 11rem, (min-width: 640px) 9rem, 8rem" />
                  </div>
                  <p className="text-white text-sm sm:text-base leading-relaxed font-semibold max-w-[280px]">
                    Une sélection d'activités <strong className="font-black">uniques en France</strong> et adaptées à tous
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.16} width="100%">
                <div className="flex flex-col sm:flex-row md:flex-col items-center sm:items-start md:items-center gap-4 sm:gap-5 text-center sm:text-left md:text-center">
                  <div className="shrink-0 w-32 h-32 sm:w-36 sm:h-36 lg:w-44 lg:h-44 xl:w-48 xl:h-48 flex items-center justify-center">
                    <MediaImage src="/brand/business/food.png" alt="Restauration pour vos événements" width={384} height={315} className="w-full h-full object-contain drop-shadow-[0_10px_22px_rgba(0,0,0,0.4)]" sizes="(min-width: 1280px) 12rem, (min-width: 1024px) 11rem, (min-width: 640px) 9rem, 8rem" />
                  </div>
                  <p className="text-white text-sm sm:text-base leading-relaxed font-semibold max-w-[280px]">
                    De nombreuses possibilités de restauration pour votre événement.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.24} width="100%">
                <div className="flex flex-col sm:flex-row md:flex-col items-center sm:items-start md:items-center gap-4 sm:gap-5 text-center sm:text-left md:text-center">
                  <div className="shrink-0 w-32 h-32 sm:w-36 sm:h-36 lg:w-44 lg:h-44 xl:w-48 xl:h-48 flex items-center justify-center">
                    <MediaImage src="/brand/business/live.png" alt="Prestations événementielles : concert, animation, magicien" width={384} height={307} className="w-full h-full object-contain drop-shadow-[0_10px_22px_rgba(0,0,0,0.4)]" sizes="(min-width: 1280px) 12rem, (min-width: 1024px) 11rem, (min-width: 640px) 9rem, 8rem" />
                  </div>
                  <p className="text-white text-sm sm:text-base leading-relaxed font-semibold max-w-[280px]">
                    Des prestations pour tous les goûts : Concert, Animation, Magicien…
                  </p>
                </div>
              </Reveal>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-[#111722] font-black text-sm px-8 py-4 rounded-full uppercase tracking-widest shadow-lg"
              >
                Créer mon événement <ArrowRight size={16} strokeWidth={3} />
              </Link>
              <Link
                to="/entreprises/team-building"
                className="inline-flex items-center justify-center border-2 border-white/35 hover:border-white text-white font-black text-sm px-8 py-4 rounded-full uppercase tracking-widest backdrop-blur-sm bg-black/20 transition-colors"
              >
                Découvrir nos formules
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative w-full bg-white pt-20 pb-[calc(5vw+3rem)] lg:pb-[calc(5vw+4rem)] px-6 lg:px-12 overflow-hidden"
        style={{ clipPath: 'polygon(0 5vw, 100% 0, 100% 100%, 0 100%)' }}
      >
        <div
          aria-hidden="true"
          className="absolute left-8 top-10 hidden xl:block h-24 w-36 opacity-[0.12]"
          style={{
            backgroundImage: 'linear-gradient(rgba(250,204,21,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(250,204,21,0.6) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-center">
          <div className="w-full max-w-4xl text-center md:text-left mb-16">
            <Reveal>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#1a1b24] mb-6 uppercase">
                NOS FORMULES ÉVÉNEMENTS <span className="gameside-accent">À CAEN</span>
              </h2>
            </Reveal>
            <p className="text-gray-600 leading-relaxed text-sm md:text-base border-l-2 border-yellow-400 pl-4">
              {renderInlineMarkdown(getText(formulasSection, 'text'))}
            </p>
          </div>

          <div className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
            {formulas.map((formula) => (
              <FormulaCard key={formula.title} {...formula} />
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full flex flex-col lg:flex-row shadow-2xl -mt-[5vw]" style={{ clipPath: 'polygon(0 5vw, 100% 0, 100% 100%, 0 100%)' }}>
        <div className="w-full lg:w-[45%] relative h-[620px] lg:h-auto min-h-[620px] xl:min-h-[800px] bg-black overflow-hidden">
          <LazyYouTubeEmbed
            videoId="n9Sk-nRVqKI"
            title="Activité Caen vidéo Action Game Gameside"
            poster={getText(local, 'poster', content.images.home.sidebarFeature)}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/15 to-transparent pointer-events-none" />
        </div>

        <div className="w-full lg:w-[55%] bg-[#A7C834] px-8 lg:px-16 pt-24 pb-20 lg:pt-32 lg:pb-32 relative overflow-hidden flex flex-col justify-center">
          <WatermarkS
            className="absolute -right-40 -bottom-32 w-[900px] hidden md:block"
            color="#1a1b24"
            opacity={0.12}
          />
          <div className="relative z-10 max-w-3xl">
            <h2 className="text-[#1a1b24] font-black text-3xl lg:text-4xl uppercase mb-6">
              {getText(local, 'title', 'GAMESIDE À CAEN, À IFS')}
            </h2>
            <div className="space-y-5 text-[#1a1b24] text-sm md:text-base leading-relaxed font-medium mb-12 opacity-90">
              <p>
                {renderInlineMarkdown(localParagraphs[0], { linkClassName: 'underline decoration-2 underline-offset-4' })}
              </p>
              {localParagraphs.slice(1).map((paragraph) => (
                <p key={paragraph}>
                  {renderInlineMarkdown(paragraph, { linkClassName: 'underline decoration-2 underline-offset-4' })}
                </p>
              ))}
            </div>

            <div className="mt-12 flex justify-end">
              <Link to={getText(local, 'ctaHref', '/contact')} className="flex items-center gap-2 text-[#1a1b24] font-bold text-sm uppercase hover:underline decoration-2 underline-offset-4">
                {getText(local, 'ctaLabel', 'DEMANDER UN DEVIS')} <ArrowRight size={16} strokeWidth={3} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}


function ActivityFeatureCard({
  href,
  image,
  logo,
  title,
  text,
  icon,
}: {
  href: string;
  image: string;
  logo?: string;
  title: string;
  text: string;
  icon: 'action' | 'karaoke';
}) {
  return (
    <div
      className="group relative block w-full min-h-[280px] lg:min-h-[360px] lg:self-center overflow-visible rounded-[34px] bg-[#14151f]
        transition-[transform,box-shadow] duration-500 ease-out
        hover:-translate-y-[3px]
        hover:shadow-[0_22px_50px_rgba(20,21,31,0.18)]"
    >
      <Link to={href} aria-label={`Découvrir ${title}`} className="absolute inset-0 z-10 rounded-[34px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400" />
      <div className="pointer-events-none absolute -left-5 -top-5 z-30 h-16 w-16 rounded-full bg-white shadow-[0_16px_38px_rgba(20,21,31,0.18)] flex items-center justify-center
        transition-[box-shadow] duration-500 group-hover:shadow-[0_16px_38px_rgba(247,181,0,0.25)]">
        {icon === 'karaoke' ? (
          <Mic2 className="h-8 w-8 text-[#1a1b24]" strokeWidth={2.6} />
        ) : (
          <MediaImage
            src="/brand/icons/physique.svg"
            alt="Activité Caen icône Action Game physique"
            title="Activité Caen Action Game physique"
            className="h-9 w-9 object-contain"
            style={{ filter: 'brightness(0) saturate(100%) invert(12%) sepia(11%) saturate(950%) hue-rotate(196deg)' }}
          />
        )}
      </div>

      <div className="relative h-full min-h-[280px] lg:min-h-[360px] overflow-hidden rounded-[34px] rounded-tr-[72px] rounded-br-[72px] bg-[#14151f] shadow-[0_20px_55px_rgba(20,21,31,0.18)]">
        <MediaImage
          src={image}
          alt={icon === 'karaoke' ? 'Groupe chantant au karaoké, visuel d’ambiance' : 'Activité Caen salle Action Game Gameside'}
          title={icon === 'karaoke' ? 'Karaoké privé Gameside Caen' : 'Activité Caen Action Game Gameside'}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          sizes="(min-width: 1024px) 760px, 100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111722]/25 via-[#111722]/55 to-[#111722]/96" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111722]/88 via-transparent to-[#111722]/20" />

        {/* Shimmer de lumière au hover */}
        <div className="action-card-shimmer" />

        <div className="pointer-events-none relative z-20 flex h-full min-h-[280px] lg:min-h-[360px] flex-col justify-end p-7 sm:p-9 lg:p-10">
          {logo && (
            <div className="absolute right-7 top-8 sm:right-9 lg:right-10 w-28 sm:w-36 transition-[opacity,transform] duration-500 group-hover:opacity-90">
              <MediaImage
                src={logo}
                alt="Activité Caen logo Action Game Gameside"
                title="Activité Caen logo Action Game Gameside"
                width={900}
                height={234}
                className="h-auto w-full object-contain drop-shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
                sizes="(min-width: 640px) 9rem, 7rem"
              />
            </div>
          )}

          <div className="max-w-[80%] sm:max-w-[76%] lg:max-w-[82%]">
            <h3 className="mb-4 text-3xl sm:text-4xl lg:text-[38px] xl:text-[42px] font-black uppercase leading-none text-white drop-shadow-md
              transition-[letter-spacing] duration-500 group-hover:tracking-wide">
              {title}
            </h3>
            <p className="text-white/90 text-sm sm:text-lg lg:text-base xl:text-lg font-medium leading-relaxed drop-shadow-md [&_a]:pointer-events-auto">
              {renderInlineMarkdown(text)}
            </p>
          </div>

          <div className="absolute bottom-7 right-7 sm:bottom-9 sm:right-9 lg:bottom-10 lg:right-10 text-white
            transition-[transform,opacity] duration-300
            group-hover:translate-x-2">
            <ArrowRight size={48} strokeWidth={3.2} />
          </div>
        </div>
      </div>
    </div>
  );
}

function WhyComeSection({ desktopImage, facts }: { desktopImage: string; facts: Record<string, string>[] }) {
  return (
    <section className="relative w-full bg-white pb-20 pt-10">
      <div
        className="relative w-full overflow-hidden min-h-[620px] sm:min-h-[520px] lg:min-h-[350px] shadow-[0_24px_70px_rgba(20,21,31,0.18)] md:[clip-path:polygon(0_8%,100%_0,100%_92%,0_100%)]"
      >
        <MediaImage
          src={desktopImage}
          alt="Action Game Caen ambiance de jeu Gameside"
          className="absolute inset-0 w-full h-full object-cover opacity-38"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/95 via-orange-400/90 to-orange-500/95" />
        <div className="relative z-10 max-w-[1600px] mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-y-14 gap-x-4 px-6 sm:px-10 lg:px-14 pt-28 pb-32 sm:py-24 lg:py-24 text-white">
          {facts.map((fact) => (
            <div key={fact.label} className="flex flex-col items-center text-center">
              <div className="h-20 lg:h-24 w-28 lg:w-32 flex items-end justify-center mb-4">
                <MediaImage
                  src={fact.icon}
                  alt={`Action Game Caen ${fact.value}${fact.unit ? ` ${fact.unit}` : ''} ${fact.label.toLowerCase()}`}
                  title={`Action Game Caen ${fact.value}${fact.unit ? ` ${fact.unit}` : ''} ${fact.label.toLowerCase()}`}
                  loading="eager"
                  className="w-full h-full object-contain drop-shadow-[0_8px_18px_rgba(0,0,0,0.12)]"
                  sizes="8rem"
                />
              </div>
              <CountUpValue value={fact.value} unit={fact.unit} />
              <div className="mt-3 text-sm lg:text-base font-black uppercase tracking-wide leading-tight">
                {fact.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const ComingSoonCard: React.FC<{ title: string; text: string }> = ({ title, text }) => {
  return (
    <div className="group relative min-h-[250px] rounded-2xl overflow-hidden bg-white flex flex-col justify-end p-7
      shadow-[0_8px_30px_rgba(0,0,0,0.05)]
      transition-[transform,box-shadow] duration-[400ms] ease-out
      hover:-translate-y-[5px]
      hover:shadow-[0_22px_50px_rgba(20,21,31,0.12)]">
      {/* Barre accent jaune gauche */}
      <div className="card-coming-accent" />

      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white
        transition-opacity duration-[400ms]
        group-hover:opacity-0" />
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 to-white opacity-0
        transition-opacity duration-[400ms]
        group-hover:opacity-100" />

      <div className="absolute top-5 right-5 z-10 inline-flex items-center gap-2 rounded-full bg-[#1a1b24] px-3 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-white shadow-sm">
        <Lock size={13} />
        Bientôt
      </div>
      <div className="relative z-10">
        <p className="text-gray-400 text-xs font-black uppercase tracking-[0.24em] mb-3">Prochainement</p>
        <h3 className="text-[#1a1b24] font-black text-2xl uppercase mb-3
          transition-[letter-spacing] duration-[400ms] group-hover:tracking-wide">
          {title}
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed">{renderInlineMarkdown(text)}</p>
      </div>
    </div>
  );
}

function FormulaCard({ image, secondaryImage, bannerColor, title, text, link }: any) {
  const isAnniversaire = typeof title === 'string' && title.toLowerCase().includes('anniversaire');
  const effectiveBanner = isAnniversaire ? 'bg-yellow-400' : bannerColor;
  const isLightBanner = effectiveBanner === 'bg-yellow-400';
  return (
    <Link
      to={link}
      className="group relative block w-full min-h-[300px] lg:min-h-[340px] overflow-visible rounded-[28px] bg-[#14151f]
        transition-[transform,box-shadow] duration-500 ease-out
        hover:-translate-y-[3px]
        hover:shadow-[0_22px_50px_rgba(20,21,31,0.18)]"
    >
      <div className={`absolute -left-4 -top-4 z-30 inline-flex items-center gap-2 ${effectiveBanner} px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.22em] shadow-[0_12px_30px_rgba(20,21,31,0.25)]
        ${isLightBanner ? 'text-[#1a1b24]' : 'text-white'}
        transition-transform duration-500 group-hover:-translate-y-[2px]`}>
        Formule
      </div>

      <div className="relative h-full min-h-[300px] lg:min-h-[340px] overflow-hidden rounded-[28px] bg-[#14151f] shadow-[0_20px_55px_rgba(20,21,31,0.18)]">
        {secondaryImage ? (
          <DiagonalFormulaImage
            leftImage={image}
            rightImage={secondaryImage}
            alt={`Activité Caen ${title} chez Gameside`}
            className="transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            sizes="(min-width: 768px) 500px, 100vw"
          />
        ) : (
          <MediaImage
            src={image}
            alt={`Activité Caen ${title} chez Gameside`}
            title={`Activité Caen ${title} Gameside`}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            sizes="(min-width: 768px) 500px, 100vw"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#111722]/25 via-[#111722]/55 to-[#111722]/96" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111722]/88 via-transparent to-[#111722]/20" />

        {/* Shimmer de lumière au hover */}
        <div className="action-card-shimmer" />

        <div className="relative z-20 flex h-full min-h-[300px] lg:min-h-[340px] flex-col justify-end p-7 sm:p-8 lg:p-9">
          <div className="max-w-[78%]">
            <h3 className="mb-3 text-2xl sm:text-3xl lg:text-[34px] font-black uppercase leading-none text-white drop-shadow-md
              transition-[letter-spacing] duration-500 group-hover:tracking-wide">
              {title}
            </h3>
            <p className="text-white/90 text-sm sm:text-base font-medium leading-relaxed drop-shadow-md">
              {renderInlineMarkdown(text)}
            </p>
          </div>

          <div className="absolute bottom-7 right-7 sm:bottom-8 sm:right-8 lg:bottom-9 lg:right-9 text-white
            transition-[transform,opacity] duration-300
            group-hover:translate-x-2">
            <ArrowRight size={40} strokeWidth={3.2} />
          </div>
        </div>
      </div>
    </Link>
  );
}
